// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { route } from "@routes/helper";
import { push } from "connected-react-router";
import classNames from "classnames";
import actionCreators from "@modules/service/actionCreator";
import type { DynamicQuestion } from "@modules/service/reducer/dynamic-questions/types";
import type { State } from "@store/reducers";
import * as serviceSelectors from "@modules/service/selectors";
import { Icons, ButtonGroup, Button, GSGrid, View, Text, Loader } from "@components";
import { AddDynamicQuestionWizardModal } from "@modules/service/components";
import { toTitleCase, transformBooleanToString } from "@transformers/response";
import { parseSchema } from "@utils/schema";

type PropsFromState = $Exact<{
  list: Array<DynamicQuestion>,
  showAddDynamicQuestionModal: boolean,
  modalStep: number,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setDynamicQuestionRevisionSchema: typeof actionCreators.SetDynamicQuestionRevisionSchema,
  getDynamicQuestions: typeof actionCreators.GetDynamicQuestions,
  setShowAddDynamicQuestionModal: typeof actionCreators.SetShowAddDynamicQuestionModal,
  newDynamicQuestion: typeof actionCreators.NewDynamicQuestion,
  push: typeof push,
}>;

type Props = $Exact<{
  clientId: number,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type FilterState = "ALL" | "ACTIVE" | "INACTIVE";

export function DynamicQuestionsSection(props: Props) {
  const [filter, setFilter]: [FilterState, Function] = useState("ACTIVE");
  const columns = [
    {
      key: "id",
      name: props.t("ID"),
      align: "center",
      width: 80,
    },
    {
      key: "questionText",
      name: props.t("QUESTION"),
      width: "30%",
      align: "left",
      renderCell: ({ row }: { row: DynamicQuestion }) =>
        GSGrid.render.ellipsis(row.content.questionText),
    },
    {
      name: props.t("DATE_ADDED"),
      key: "createdAt",
      width: 100,
      renderCell: ({ row }: { row: DynamicQuestion }) => GSGrid.render.date(row.createdAt),
    },
    {
      name: props.t("ADDED_BY"),
      key: "addedBy",
      width: 150,
      renderCell: ({ row }: { row: DynamicQuestion }) =>
        GSGrid.render.ellipsis(
          row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : "",
        ),
      align: "left",
    },

    {
      key: "questionType",
      name: props.t("QUESTION_TYPE"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_DQNAMIC_QUESTIONS_TABLE_QUESTION_TYPE"),
      },
      renderCell: ({ row }: { row: DynamicQuestion }) => {
        const questionType = getValuesAndScore(row).questionType;
        return getColorPillRender(questionType.value, questionType.isScoring);
      },
    },
    {
      key: "isMandatory",
      name: props.t("MANDATORY"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_DQNAMIC_QUESTIONS_TABLE_MANDATORY"),
      },
      renderCell: ({ row }: { row: DynamicQuestion }) => {
        const isMandatory = getValuesAndScore(row).isMandatory;
        return getColorPillRender(isMandatory.value, isMandatory.isScoring);
      },
    },
    {
      key: "textLength",
      name: props.t("TEXT_LENGTH"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_DQNAMIC_QUESTIONS_TABLE_TEXT_LENGTH"),
      },
      renderCell: ({ row }: { row: DynamicQuestion }) => {
        const textLength = getValuesAndScore(row).textLength;
        return getColorPillRender(textLength.value, textLength.isScoring);
      },
    },
    {
      key: "hierarchyAssignments",
      name: props.t("HIERARCHY_AREAS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_DQNAMIC_QUESTIONS_TABLE_HIERARCHY_AREAS"),
      },
      renderCell: ({ row }: { row: DynamicQuestion }) => {
        const hierarchyAssignments = getValuesAndScore(row).hierarchyAssignments;
        return getColorPillRender(hierarchyAssignments.value, hierarchyAssignments.isScoring);
      },
    },
    {
      key: "updateNotifications",
      name: props.t("NOTIFICATIONS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_DQNAMIC_QUESTIONS_TABLE_NOTIFICATIONS"),
      },
      renderCell: ({ row }: { row: DynamicQuestion }) => {
        const updateNotifications = getValuesAndScore(row).updateNotifications;
        return getColorPillRender(updateNotifications.value, updateNotifications.isScoring);
      },
    },

    {
      key: "rating",
      name: props.t("RATING"),
      renderCell: ({ row }: { row: DynamicQuestion }) => {
        const valuesAndScore = Object.values(getValuesAndScore(row));

        if (!Array.isArray(valuesAndScore)) return;
        //$FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },

    {
      key: "status",
      name: props.t("STATUS"),
      resizable: false,
      width: 100,
      renderCell: ({ row }: { row: DynamicQuestion }) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      key: "actions",
      width: 50,
      resizable: false,
      name: props.t("VIEW"),
      onCellClick: (row: DynamicQuestion) => openDynamicQuestionDetails(row.id),
      renderCell: ({ row }: { row: DynamicQuestion }) => GSGrid.render.materialIcon("search"),
    },
  ];

  type ValueAndScore = {
    value: any,
    isScoring: boolean,
  };
  type ValuesAndScoreRecord = {
    questionType: ValueAndScore,
    isMandatory: ValueAndScore,
    textLength: ValueAndScore,
    hierarchyAssignments: ValueAndScore,
    updateNotifications: ValueAndScore,
  };

  function getValuesAndScore(row: DynamicQuestion): ValuesAndScoreRecord {
    const questionType = row.content.questionType;
    const isMandatory = row.content.isMandatory;
    const textLength = row.content.questionText;
    const hierarchyAssignments = row.hierarchyAssignments || [];
    const updateNotifications = row.content.updateNotification;

    return {
      questionType: {
        value: toTitleCase(questionType.replace("_", " ")),
        isScoring: questionType !== "FREE_TEXT",
      },
      isMandatory: {
        value: props.t(isMandatory ? "YES" : "NO"),
        isScoring: !!isMandatory,
      },
      textLength: {
        value: textLength.length,
        isScoring: textLength.length <= 100,
      },
      hierarchyAssignments: {
        value: hierarchyAssignments.length,
        isScoring: hierarchyAssignments.length > 0,
      },
      updateNotifications: {
        value: props.t(updateNotifications ? "YES" : "NO"),
        isScoring: !!updateNotifications,
      },
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  useEffect(() => {
    props.getDynamicQuestions({
      clientId: props.clientId,
      isActive: filter !== "ALL" ? filter == "ACTIVE" : null,
    });
  }, [filter, props.clientId]);

  function handleChangeFilter(filter: FilterState) {
    return function() {
      setFilter(filter);
    };
  }

  function openDynamicQuestionDetails(id: number) {
    props.push(
      route(`ADMINISTRATION_SERVICE_DYNAMIC_QUESTION_DETAILS`, {
        clientId: props.clientId,
        dynamicQuestionId: id,
      }),
    );
  }

  function handleAddDynamicQuestion() {
    props.newDynamicQuestion();
    props.setShowAddDynamicQuestionModal({ show: true, step: 1 });
  }

  return (
    <View className="gs-container">
      {props.isLoading && <Loader />}
      <View margin="0 0 1rem 0">
        <Text type="h6" text={props.t("DYNAMIC_QUESTIONS")} />
      </View>
      <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
        <View direction="row">
          <ButtonGroup>
            <Button
              color={filter === "ALL" ? "selected" : null}
              onClick={handleChangeFilter("ALL")}
            >
              {props.t("ALL")}
            </Button>
            <Button
              color={filter === "ACTIVE" ? "selected" : null}
              onClick={handleChangeFilter("ACTIVE")}
            >
              {props.t("ACTIVE")}
            </Button>
            <Button
              color={filter === "INACTIVE" ? "selected" : null}
              onClick={handleChangeFilter("INACTIVE")}
            >
              {props.t("INACTIVE")}
            </Button>
          </ButtonGroup>
        </View>
        <View direction="row">
          <Button materialIcon="add_circle" onClick={handleAddDynamicQuestion} size="small">
            {props.t("ADD")}
          </Button>
        </View>
      </View>
      <View>
        <GSGrid columns={columns} rows={props.list} />
        {props.showAddDynamicQuestionModal && (
          <AddDynamicQuestionWizardModal
            showModal={true}
            clientId={props.clientId}
            defaultStep={props.modalStep}
            filter={filter}
          />
        )}
      </View>
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: serviceSelectors.isLoading(state),
    list: serviceSelectors.getDynamicQuestionsList(state),
    showAddDynamicQuestionModal: serviceSelectors.getShowAddDynamicQuestionModal(state),
    modalStep: serviceSelectors.getDynamicQuestionsModalStep(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDynamicQuestionRevisionSchema: actionCreators.SetDynamicQuestionRevisionSchema,
  getDynamicQuestions: actionCreators.GetDynamicQuestions,
  setShowAddDynamicQuestionModal: actionCreators.SetShowAddDynamicQuestionModal,
  newDynamicQuestion: actionCreators.NewDynamicQuestion,
  push,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(DynamicQuestionsSection);
